import { Lightbulb, Percent } from "lucide-react";

import { Input } from "@/components/ui/input";
import { InputDecimal } from "@/components/ui/input-decimal";
import { Label } from "@/components/ui/label";

import Cartao from "../components/Cartao";
import { tipoDaForma } from "../lib/sugestoes";
import { pct, rotuloPrazo } from "../lib/simulacao";

const PRAZOS = [0, 1, 2, 14, 30];

function Sugestoes({ tipo, aoEscolher }) {
  if (!tipo) return null;
  return (
    <div className="rounded-xl bg-amber-500/5 border border-amber-500/20 p-3">
      <p className="text-xs font-semibold flex items-center gap-1.5 text-amber-700 dark:text-amber-400">
        <Lightbulb className="w-3.5 h-3.5" />Valores comuns para {tipo.rotulo} — confira no seu contrato
      </p>
      <div className="flex flex-wrap gap-2 mt-2">
        {tipo.sugestoes.map((s) => (
          <button key={s.nome} type="button" onClick={() => aoEscolher(s)} data-e2e="sugestao-taxa"
            className="text-xs px-2.5 py-1.5 rounded-lg border bg-background hover:bg-muted transition-colors text-left">
            <span className="font-semibold">{pct(s.taxa)} · D+{s.prazo}</span>
            <span className="text-muted-foreground"> — {s.nome}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function AvisoVigencia({ taxaAtual }) {
  const texto = taxaAtual?.configurada
    ? "Mudou a taxa? A nova vale a partir de hoje. Vendas antigas continuam com a taxa da época — o DRE do mês passado não muda."
    : "Primeira vez configurando: esta taxa vale também para as vendas antigas, no DRE e no Fluxo de Caixa.";
  return <p className="text-[11px] text-muted-foreground leading-relaxed">{texto}</p>;
}

/** Taxa (%), taxa fixa por venda e prazo de recebimento — alimentam o DRE e o Fluxo de Caixa. */
export default function SecaoTaxa({ d, nomeTipo }) {
  const { taxa } = d.form;
  const set = (campo, v) => d.definirSub("taxa", campo, v);
  const tipo = tipoDaForma(nomeTipo || d.form.name, d.form.code);
  const escolher = (s) => { set("taxa_percentual", String(s.taxa)); set("taxa_fixa", "0"); set("prazo_dias", String(s.prazo)); };
  const prazo = parseInt(taxa.prazo_dias, 10) || 0;
  return (
    <Cartao titulo="Taxa e recebimento" icone={Percent} destaque e2e="secao-taxa"
      subtitulo="Quanto a maquininha, o banco ou o marketplace cobra, e em quantos dias o dinheiro cai na conta.">
      <div className="space-y-4">
        <Sugestoes tipo={tipo} aoEscolher={escolher} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label>Taxa sobre a venda</Label>
            <div className="mt-1 flex items-center gap-2">
              <div className="flex-1"><InputDecimal decimals={2} value={taxa.taxa_percentual} onChange={(v) => set("taxa_percentual", v)} data-e2e="campo-taxa" /></div>
              <span className="text-muted-foreground text-sm font-semibold">%</span>
            </div>
          </div>
          <div>
            <Label>Taxa fixa por venda <span className="text-muted-foreground font-normal">(opcional)</span></Label>
            <div className="mt-1 flex items-center gap-2">
              <span className="text-muted-foreground text-sm">R$</span>
              <div className="flex-1"><InputDecimal decimals={2} value={taxa.taxa_fixa} onChange={(v) => set("taxa_fixa", v)} data-e2e="campo-taxa-fixa" /></div>
            </div>
          </div>
        </div>
        <div>
          <Label>O dinheiro cai na conta</Label>
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            {PRAZOS.map((p) => (
              <button key={p} type="button" onClick={() => set("prazo_dias", String(p))}
                className={`h-9 px-3 rounded-lg border text-sm font-medium transition-colors ${prazo === p ? "bg-primary text-primary-foreground border-primary" : "hover:bg-muted"}`}>
                {p === 0 ? "Na hora" : `D+${p}`}
              </button>
            ))}
            <div className="flex items-center gap-1.5 text-sm">
              <Input type="number" min="0" max="120" value={taxa.prazo_dias} onChange={(e) => set("prazo_dias", e.target.value)}
                className="w-20 h-9 text-center" data-e2e="campo-prazo" />
              <span className="text-muted-foreground">dias</span>
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground mt-1.5">Recebe {rotuloPrazo(prazo)} depois da venda (dias corridos).</p>
        </div>
        <AvisoVigencia taxaAtual={d.info.taxaAtual} />
      </div>
    </Cartao>
  );
}
